import { Injectable } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { getLoggerFor, LogAs, LogInvocation, LogLevel, LogName } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, CompareType, PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject } from "../interfaces";
import { ObjectHelpers } from "../utilities";
import { MetadataService } from "./metadata.service";
import { MetadataInputTypeHelperService } from "./metadataInputTypeHelperService";

type OperatorMessage = "less than" | "less than or equal to" | "equal to" | "greater than or equal to" | "greater than";

/**
 * @private NOT FOR EXTERNAL USE
 * This is a front-end service only.
 */
@Injectable({ providedIn: "root" })
export class MetadataValidationService
{
    private readonly log = getLoggerFor("MetadataValidationService");

    constructor(
        private readonly $metadataService: MetadataService,
        private $metadataInputTypeHelperService: MetadataInputTypeHelperService
    ) { }

    /**
     * Evaluates custom metadata validators for the given property against the supplied model.
     * This will cause immediately-related properties to be re-evaluated.
     *
     * @param control AbstractControl for the HTML Input input field.
     * @param classMetadata ClassMetadata object that described the model object.
     * @param property PropertyMetadata object for the property being validated.
     * @param model Object being validated.
     * @param parent Optional object that the model belongs to.
     */
    @LogInvocation(LogLevel.DEBUG)
    validate(
        control: AbstractControl,
        classMetadata: AugmentedClassMetadata,
        property: AugmentedPropertyMetadata,
        model: AugmentedModelObject,
        parent?: AugmentedModelObject
    ): void
    {
        this.validateImpl(true, control, classMetadata, property, model, parent);
    }

    /**
     * Evaluates custom metadata validators for the given property against the supplied model.
     *
     * @param shouldRevalidateRelatedProperties Controls whether or not related Properties will be re-evaluated.
     * @param ngModelCtrl `ng-model` controller for the HTML Input input field.
     * @param classMetadata ClassMetadata object that described the model object.
     * @param property PropertyMetadata object for the property being validated.
     * @param model Object being validated.
     * @param parent Optional object that the model belongs to.
     */
    @LogInvocation(LogLevel.DEBUG)
    private validateImpl(
        @LogName("shouldRevalidateRelatedProperties") shouldRevalidateRelatedProperties: boolean,
        @LogName("control") control: AbstractControl,
        @LogAs((x: ClassMetadata) => `[ClassMetadata "${x.TypeName}"]`) classMetadata: AugmentedClassMetadata,
        @LogAs((x: PropertyMetadata) => `[PropertyMetadata "${x.Name}"]`) property: AugmentedPropertyMetadata,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) model: AugmentedModelObject,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) parent?: AugmentedModelObject
    ): void
    {
        if (shouldRevalidateRelatedProperties)
        {
            this.validateRelatedProperties(classMetadata, property, model, parent);
        }
        else
        {
            if (property.ComparedTo)
            {
                this.evaluateCompareTo(control, classMetadata, property, model, parent);
            }

            if (property.MinValueProperty || property.MaxValueProperty)
            {
                control["$runValidation"] = true;
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    private validateRelatedProperties(
        @LogAs((x: ClassMetadata) => `[ClassMetadata "${x.TypeName}"]`) classMetadata: AugmentedClassMetadata,
        @LogAs((x: PropertyMetadata) => `[PropertyMetadata "${x.Name}"]`) property: AugmentedPropertyMetadata,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) model: AugmentedModelObject,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) parent?: any
    ): void
    {
        if (!property.$relatedPropertyNames?.length)
            return;

        for (const propName of property.$relatedPropertyNames)
        {
            const otherProperty = getPropertyMetadata(classMetadata, propName);
            if (!otherProperty)
            {
                this.log.error(`model object doesn't have a matching property; propName was ${JSON.stringify(propName)}`);
                continue;
            }

            const otherCtrl = getCachedControl(classMetadata, propName);
            if (!otherCtrl)
            {
                this.log.warning(`We have not cached a form control for that property; propName was ${JSON.stringify(propName)}`);
                continue;
            }

            // ? TODO: why do we have to touch the control during validation?
            otherCtrl.markAsTouched();
            this.validateImpl(false, otherCtrl, classMetadata, otherProperty, model, parent);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    protected evaluateCompareTo(
        @LogAs(_ => `[AbstractControl]`) control: AbstractControl,
        @LogAs((x: ClassMetadata) => `[ClassMetadata "${x.TypeName}"]`) classMetadata: AugmentedClassMetadata,
        @LogAs((x: PropertyMetadata) => `[PropertyMetadata "${x.Name}"]`) property: AugmentedPropertyMetadata,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) model: AugmentedModelObject,
        @LogAs((x: AugmentedModelObject) => `[ModelObject "${x?.$$TypeName}"]`) parent?: AugmentedModelObject
    ): void
    {
        const compareToProperty = getPropertyMetadata(classMetadata, property.ComparedTo);
        if (property.InputType !== compareToProperty.InputType)
        {
            throw new Error(
                `Cannot compare types; types were ${JSON.stringify(property.InputType)} and ${JSON.stringify(
                    compareToProperty.InputType
                )}.`
            );
        }

        //only doing FE validation on certain InputTypes for now. other scenarios won't be hit until we decide to support more
        if (this.$metadataInputTypeHelperService.isTextBased(property.InputType)
            && property.CompareType !== CompareType.EqualTo)
        {
            const equalTo = CompareType[CompareType.EqualTo];
            const operator = CompareType[property.CompareType];
            throw new Error(
                `Invalid InputType and CompareType combination. Text-based Inputs can only use ${equalTo}; CompareType was ${JSON.stringify(operator)}.`
            );
        }
        else if (!this.$metadataInputTypeHelperService.isDate(property.InputType)
            && !this.$metadataInputTypeHelperService.isNumeric(property.InputType))
        {
            throw new Error(`Type was not a comparable type; type was ${JSON.stringify(property.InputType)}.`);
        }

        //let thisModel = JSON.stringify(model);
        let thisModel = this.$metadataService.DependentModuleValue(property.Name, parent, model);
        let compareModel = this.$metadataService.DependentModuleValue(compareToProperty.Name, parent, model);

        const areDates = this.$metadataInputTypeHelperService.isDate(property.InputType);
        if (areDates)
        {
            thisModel = toDate(thisModel);
            compareModel = toDate(compareModel);

            this.log.debug(`evaluateCompareTo | parsed date values`, { thisModel, compareModel });
        }

        let operatorMessage: OperatorMessage;
        let valid: boolean;
        switch (property.CompareType)
        {
            case CompareType.LessThan:
                operatorMessage = "less than";
                valid = thisModel < compareModel;
                this.log.debug(`evaluateCompareTo | LessThan | result = thisModel < compareModel`, { valid, thisModel, compareModel });
                break;
            case CompareType.LessThanOrEqualTo:
                operatorMessage = "less than or equal to";
                valid = thisModel <= compareModel;
                this.log.debug(`evaluateCompareTo | LessThanOrEqualTo | result = thisModel <= compareModel`, { valid, thisModel, compareModel });
                break;
            case CompareType.EqualTo:
                operatorMessage = "equal to";
                valid = areDates
                    /*
                     * have to compare times since Date's are just objects, and according to spec their equality tests identity instead of value.
                     */
                    ? (thisModel as Date).getTime() === (compareModel as Date).getTime()
                    : thisModel === compareModel;
                this.log.debug(`evaluateCompareTo | EqualTo | result = thisModel = compareModel`, { valid, thisModel, compareModel });
                break;
            case CompareType.GreaterThanOrEqualTo:
                operatorMessage = "greater than or equal to";
                valid = thisModel >= compareModel;
                this.log.debug(`evaluateCompareTo | GreaterThanOrEqualTo | result = thisModel >= compareModel`, { valid, thisModel, compareModel });
                break;
            case CompareType.GreaterThan:
                operatorMessage = "greater than";
                valid = thisModel > compareModel;
                this.log.debug(`evaluateCompareTo | GreaterThan | result = thisModel > compareModel`, { valid, thisModel, compareModel });
                break;
        }

        const errors = { "compareTo": !valid };
        this.log.debug(`Setting errors on control`, { errors, control, property, model });
        control.setErrors(errors);

        if (valid)
        {
            control.updateValueAndValidity();
        }

        if (!valid)
        {
            const errorsObj = ObjectHelpers.safeAccess(model, "$errors", {});
            const fieldErrors = ObjectHelpers.safeAccess(errorsObj, property.Name, {});
            fieldErrors["compareTo"] = `Must be ${operatorMessage} ${property.ComparedTo}`;
        }
        else if (model.$errors)
        {
            if (property.Name in model.$errors && !Utilities.IsNullOrUndefined(model.$errors[property.Name]))
            {
                delete model.$errors[property.Name];
            }
            if (
                property.ComparedTo in model.$errors &&
                !Utilities.IsNullOrUndefined(model.$errors[property.ComparedTo])
            )
            {
                delete model.$errors[property.ComparedTo];
            }
        }
    }

    updateRelatedProperties(classMetadata: ClassMetadata, property: PropertyMetadata, control: AbstractControl): void
    {
        if (property.ComparedTo || property.MinValueProperty || property.MaxValueProperty)
        {
            this.cacheControl(classMetadata, property.Name, control);
            if (property.ComparedTo)
                this.updateRelatedPropertiesImpl(classMetadata, property.Name, property.ComparedTo);
            if (property.MinValueProperty)
                this.updateRelatedPropertiesImpl(classMetadata, property.Name, property.MinValueProperty);
            if (property.MaxValueProperty)
                this.updateRelatedPropertiesImpl(classMetadata, property.Name, property.MaxValueProperty);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    protected updateRelatedPropertiesImpl(classMetadata: ClassMetadata, propertyName: string, comparisonPropertyName: string): void
    {
        const comparisonProperty = getPropertyMetadata(classMetadata, comparisonPropertyName);
        if (!comparisonProperty)
        {
            return;
        }

        if (comparisonPropertyName === propertyName)
        {
            return;
        }

        const otherRelatedPropertyNames = ObjectHelpers.safeAccess<string[]>(
            comparisonProperty, PROPERTY_RELATED_PROPERTIES, []);
        const doesNotContainProperty = !otherRelatedPropertyNames.includes(propertyName);
        if (doesNotContainProperty)
        {
            otherRelatedPropertyNames.push(propertyName);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    protected cacheControl(
        classMetadata: AugmentedClassMetadata,
        propertyName: string,
        control: AbstractControl
    ): void
    {
        const cache = ObjectHelpers.safeAccess(classMetadata, PROPERTY_CONTROLS, {});
        if (propertyName in cache && cache[propertyName] === control)
        {
            return;
        }
        cache[propertyName] = control;
    }

    validatePrecision(newValue: string, isDisabled: boolean, precision: number): boolean
    {
        if (!!newValue && !!precision && precision > 0 && !isDisabled)
        {
            // need to ignore 0 before the decimal as that is not part of precision when a number is <
            if (newValue.startsWith("0.") || newValue.startsWith("-0."))
            {
                const tempVal = newValue.substring(newValue.indexOf("."), newValue.length);
                const result = (tempVal.match(/\d/g) || []).length <= precision;
                return result;
            }
            const result = (newValue.match(/\d/g) || []).length <= precision;
            return result;
        }
        return true;
    }

    validateScale(newValue: string, isDisabled: boolean, scale: number): boolean
    {
        // REVIEW - explicitly allowing 0 in case we want to same integers only?
        if (!newValue || Utilities.IsNullOrUndefined(scale) || isDisabled)
            return true;
        const parts = newValue.replace("%", "")
            .split(".");
        const fractional = parts[1] || "";
        const result = fractional.length <= scale;
        return result;
    }

    validateMinimumAmount(newValue: string, isDisabled: boolean, minValue: number, parse: (stringFloatValue: string) => string): boolean
    {
        if (!newValue || Utilities.IsNullOrUndefined(minValue) || isDisabled)
            return true;
        const stringFloatValue = parse(newValue);
        const floatValue = parseFloat(stringFloatValue);
        const result = floatValue >= minValue;
        return result;
    }

    validateMaximumAmount(newValue: string, isDisabled: boolean, maxValue: number, parse: (stringFloatValue: string) => string): boolean
    {
        if (!newValue || Utilities.IsNullOrUndefined(maxValue) || isDisabled)
            return true;
        const stringFloatValue = parse(newValue);
        const floatValue = parseFloat(stringFloatValue);
        const result = floatValue <= maxValue;
        return result;
    }

    genericParse(value: string): string
    {
        if (this.isExponentialNotation(value)) return null;
        const source = value ? value.replace(/,/g, "")
            .replace(/\$/g, "")
            .replace(/%/g, "") : value;
        const results = /(-?(?:\d+(?:\.\d+)?|\.\d+))/.exec(source);
        return results && results.length > 0 ? results[1] : value;
    }

    private isExponentialNotation(value: string): boolean
    {
        if (!Utilities.IsNullOrUndefined(value))
        {
            if (value.indexOf("e+") >= 0)
            {
                return true;
            }
        }
        return false;
    }
}

function getPropertyMetadata(classMetadata: ClassMetadata, propertyName: string): AugmentedPropertyMetadata
{
    return classMetadata.Properties.find(x => x.Name === propertyName) as AugmentedPropertyMetadata;
}

function getCachedControl(
    classMetadata: AugmentedClassMetadata,
    propertyName: string
): AbstractControl
{
    const cache = ObjectHelpers.safeAccess(classMetadata, PROPERTY_CONTROLS, {});
    if (!(propertyName in cache))
    {
        return null;
    }
    return cache[propertyName];
}

function toDate(value: number | string | Date): Date
{
    const date = value instanceof Date ? value : new Date(value);
    date.setHours(0, 0, 0, 0);

    return date;
}

export const PROPERTY_CONTROLS = "$controls";

export interface AugmentedClassMetadata extends ClassMetadata
{
    $controls?: { [propertyName: string]: AbstractControl, };
}

const PROPERTY_RELATED_PROPERTIES = "$relatedPropertyNames";
export interface AugmentedPropertyMetadata extends PropertyMetadata
{
    $relatedPropertyNames?: string[];
}
